import { Feature, parseDateString, toDateString } from "@pentacode/core";
import type { MenuSelectEvent } from "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "../../elements/date-picker";
import type { DatePicker } from "../../elements/date-picker";
import { app } from "../../init";
import { canAccess, menuData } from "../../lib/route-tree";
import { Routing, routeProperty } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { mixins, shared } from "../../styles";
import { contactSupport } from "../../vendor/helpscout";
import { type PlanningPage, planningPages, planningRoute } from "../app-routes";
import { routeAccess } from "../route-access";
import "./revenue";
import "./roster";

@customElement("ptc-planning")
export class Planning extends Routing(StateMixin(LitElement)) {
    routePattern = /^planning(?:\/(?<page>\w+))?/;

    get helpPage() {
        switch (this._page) {
            case "revenue":
                return "/handbuch/planung/umsatzplanung";
            case "roster":
                return "/handbuch/planung/stundenvorgaben";
            default:
                return "/handbuch/planung";
        }
    }

    @routeProperty({ arg: "page" })
    private _page: PlanningPage;

    @state()
    private _loading = false;

    @query("select")
    private _venueSelect: HTMLSelectElement;

    @query("ptc-date-picker")
    private _datePicker: DatePicker;

    private _selectVenue() {
        this.go(null, { venue: this._venueSelect.value, date: this.date });
    }

    handleRoute() {
        if (!this.venue || !app.hasAccess({ venue: this.venue })) {
            const venue = app.venues.find((venue) => app.hasAccess({ venue }));
            if (venue) {
                this.go(null, { ...this.router.params, venue: venue.id.toString() }, true);
            }
            return;
        }

        if (!planningPages.includes(this._page) || !this._canAccessPage(this._page)) {
            const page = planningPages.find((page) => this._canAccessPage(page));
            this.go(page ? `planning/${page}` : "", undefined, true);
            return;
        }

        const today = toDateString(new Date());

        //Invalid date
        if (!this.date || !parseDateString(this.date)) {
            this.go(null, { ...this.router.params, date: today }, true);
            return;
        }

        this._datePicker && (this._datePicker.value = this.date);
    }

    private _canAccessPage(page: PlanningPage) {
        return canAccess(planningRoute(page), routeAccess());
    }

    static styles = [
        shared,
        css`
            :host {
                display: flex;
            }

            .sub-menu {
                position: relative;
                width: 220px;
                box-sizing: border-box;
            }

            .sub-menu select {
                width: 100%;
                margin-bottom: 1em;
            }

            .main {
                flex: 1;
                min-width: 0;
                position: relative;
            }

            .main > * {
                ${mixins.fullbleed()};
            }

            @media print {
                .main > * {
                    position: static;
                }
            }
        `,
    ];

    render() {
        if (!app.company || !app.company.features.includes(Feature.Planning)) {
            return html`
                <div class="poster center-aligning center-justifying vertical layout fullbleed">
                    <ptc-ui-icon name="icon.object.date"></ptc-ui-icon>

                    <div>
                        Dieser Bereich ist für Ihr Unternehmen noch nicht verfügbar! Bitte kontaktieren sie unser
                        Supportteam um zu erfahren, wie Sie dieses Modul freizuschalten können!
                    </div>

                    <button @click=${() => contactSupport("Planungsmodul Freischalten")}>
                        <ptc-ui-icon name="icon.action.contact-support"></ptc-ui-icon>
                        Support Kontaktieren
                    </button>
                </div>
            `;
        }

        return html`
            <div class="sub-menu noprint">
                <ptc-ui-menu
                    .groups=${menuData(planningPages, planningRoute, routeAccess())}
                    active=${this._page}
                    @menu-select=${(e: MenuSelectEvent) =>
                        this.go(`planning/${e.detail.id}`, { venue: this.venueId, date: this.date })}
                    ><slot name="submenu" slot="replace"></slot
                ></ptc-ui-menu>

                <div class="divider"></div>

                <select @change=${this._selectVenue}>
                    ${app.accessibleVenues.map(
                        (venue) => html`
                            <option
                                .value=${venue.id.toString()}
                                ?selected=${venue.id === this.venueId}
                                ?disabled=${!app.hasAccess({ venue })}
                            >
                                ${venue.name}
                            </option>
                        `
                    )}
                </select>

                <ptc-date-picker
                    class="small"
                    mode="week"
                    @change=${(e: CustomEvent) =>
                        this.go(null, { venue: this._venueSelect.value, date: e.detail.date })}
                ></ptc-date-picker>
            </div>

            <div class="main">
                ${this._page === "revenue" ? html`<ptc-planning-revenue></ptc-planning-revenue>` : null}
                ${this._page === "roster" ? html`<ptc-planning-roster></ptc-planning-roster>` : null}
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
